import { Check, ChevronDown, ChevronUp } from "@squaredmade/icons";
import { cn } from "@squaredmade/ui/cn";
import { createCollection } from "@squaredmade/ui/collection";
import { composeEventHandlers } from "@squaredmade/ui/compose-events";
import { useComposedRefs } from "@squaredmade/ui/compose-refs";
import { type Scope, createContextScope } from "@squaredmade/ui/context";
import { useFocusGuards } from "@squaredmade/ui/focus-guards";
import { FocusScope } from "@squaredmade/ui/focus-scope";
import { clamp } from "@squaredmade/ui/number";
import {
	Popper,
	PopperAnchor,
	PopperArrow,
	PopperContent,
	createPopperScope,
} from "@squaredmade/ui/popper";
import { Portal as PortalPrimitive } from "@squaredmade/ui/portal";
import { Primitive } from "@squaredmade/ui/primitive";
import { Slot } from "@squaredmade/ui/slot";
import { useCallbackRef } from "@squaredmade/ui/use-callback-ref";
import { useControllableState } from "@squaredmade/ui/use-controllable-state";
import { useDirection } from "@squaredmade/ui/use-direction";
import { useId } from "@squaredmade/ui/use-id";
import { useLayoutEffect } from "@squaredmade/ui/use-layout-effect";
import { usePrevious } from "@squaredmade/ui/use-previous";
import { VisuallyHidden } from "@squaredmade/ui/visually-hidden";
import { hideOthers } from "aria-hidden";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { DismissableLayer } from "src/lib/dismissable-layer";

type Direction = "ltr" | "rtl";

const OPEN_KEYS = [" ", "Enter", "ArrowUp", "ArrowDown"];
const SELECTION_KEYS = [" ", "Enter"];

/* -------------------------------------------------------------------------------------------------
 * Select
 * -----------------------------------------------------------------------------------------------*/

const SELECT_NAME = "Select";

type ItemData = { value: string; disabled: boolean; textValue: string };
const [Collection, useCollection, createCollectionScope] = createCollection<
	SelectItemElement,
	ItemData
>(SELECT_NAME);

type ScopedProps<P> = P & { __scopeSelect?: Scope };
const [createSelectContext, createSelectScope] = createContextScope(
	SELECT_NAME,
	[createCollectionScope, createPopperScope],
);
const usePopperScope = createPopperScope();

type SelectContextValue = {
	/** The current trigger element for the select component. */
	trigger: SelectTriggerElement | null;
	/** Updates the trigger element when it changes. */
	onTriggerChange(node: SelectTriggerElement | null): void;
	/** The current element that displays the selected value. */
	valueNode: SelectValueElement | null;
	/** Updates the value element when it changes. */
	onValueNodeChange(node: SelectValueElement): void;
	/** Indicates whether the value element has children. */
	valueNodeHasChildren: boolean;
	/** Updates the state of whether the value element has children. */
	onValueNodeHasChildrenChange(hasChildren: boolean): void;
	/** The unique identifier for the select content element. */
	contentId: string;
	/** The currently selected value in the select component. */
	value?: string;
	/** Callback function triggered when the selected value changes. */
	onValueChange(value: string): void;
	/** Indicates whether the select dropdown is currently open. */
	open: boolean;
	/** Specifies if selecting a value is required. */
	required?: boolean;
	/** Callback function triggered when the open state of the select dropdown changes. */
	onOpenChange(open: boolean): void;
	/** Specifies the text direction ('ltr' for left-to-right, 'rtl' for right-to-left). */
	dir: SelectProps["dir"];
	/** A reference to the position where the pointer was pressed on the trigger element. */
	triggerPointerDownPosRef: React.RefObject<{ x: number; y: number } | null>;
	/** Indicates whether the select component is disabled. */
	disabled?: boolean;
};

const [SelectProvider, useSelectContext] =
	createSelectContext<SelectContextValue>(SELECT_NAME);

type NativeOption = React.ReactElement<React.ComponentProps<"option">>;

type SelectNativeOptionsContextValue = {
	onNativeOptionAdd(option: NativeOption): void;
	onNativeOptionRemove(option: NativeOption): void;
};
const [SelectNativeOptionsProvider, useSelectNativeOptionsContext] =
	createSelectContext<SelectNativeOptionsContextValue>(SELECT_NAME);

interface SelectProps {
	/** The content to be rendered inside the select component. */
	children?: React.ReactNode;

	/** The controlled value of the select component. */
	value?: string;

	/** The default selected value for the uncontrolled select component. */
	defaultValue?: string;

	/** Callback function triggered when the selected value changes. */
	onValueChange?(value: string): void;

	/** Controls whether the select dropdown is open. */
	open?: boolean;

	/** The default open state of the select dropdown for uncontrolled usage. */
	defaultOpen?: boolean;

	/** Callback function triggered when the open state of the select dropdown changes. */
	onOpenChange?(open: boolean): void;

	/** Specifies the text direction ('ltr' for left-to-right, 'rtl' for right-to-left). */
	dir?: Direction;

	/** The name of the select component, used for form submission. */
	name?: string;

	/** The autoComplete attribute for the select component. */
	autoComplete?: string;

	/** Indicates whether the select component is disabled. */
	disabled?: boolean;

	/** Specifies whether a value selection is required. */
	required?: boolean;

	/** The form with which the select component is associated. */
	form?: string;
}

/**
 * Select component for dropdown selection
 *
 * The Select component provides a customizable dropdown menu for selecting options. It's designed to be flexible and accessible, supporting keyboard navigation and screen readers.
 *
 * Key features:
 * - Customizable trigger and dropdown content
 * - Support for grouping options
 * - Accessible keyboard navigation
 * - Customizable styling through className props
 * - Indicator for selected items
 *
 * Usage considerations:
 * - Use when you need users to choose one option from a list of choices
 * - Provide clear labels for the select and its options
 * - Consider using a placeholder value when no option is selected
 * - Ensure the dropdown is large enough for easy interaction on touch devices
 * - Use Select.Group and Select.Label for organizing long lists of options
 */
const Select: React.FC<SelectProps> = (props: ScopedProps<SelectProps>) => {
	const {
		__scopeSelect,
		children,
		open: openProp,
		defaultOpen,
		onOpenChange,
		value: valueProp,
		defaultValue,
		onValueChange,
		dir,
		name,
		autoComplete,
		disabled,
		required,
		form,
	} = props;
	const popperScope = usePopperScope(__scopeSelect);
	const [trigger, setTrigger] = React.useState<SelectTriggerElement | null>(
		null,
	);
	const [valueNode, setValueNode] = React.useState<SelectValueElement | null>(
		null,
	);
	const [valueNodeHasChildren, setValueNodeHasChildren] = React.useState(false);
	const direction = useDirection(dir);
	const [open = false, setOpen] = useControllableState({
		prop: openProp,
		defaultProp: defaultOpen,
		onChange: onOpenChange,
	});
	const [value, setValue] = useControllableState({
		prop: valueProp,
		defaultProp: defaultValue,
		onChange: onValueChange,
	});
	const triggerPointerDownPosRef = React.useRef<{
		x: number;
		y: number;
	} | null>(null);

	// We set this to true by default so that events bubble to forms without JS (SSR)
	const isFormControl = trigger ? form || !!trigger.closest("form") : true;
	const [nativeOptionsSet, setNativeOptionsSet] = React.useState(
		new Set<NativeOption>(),
	);

	// The native `select` only associates the correct default value if the corresponding
	// `option` is rendered as a child **at the same time** as itself.
	// Because it might take a few renders for our items to gather the information to build
	// the native `option`(s), we generate a key on the `select` to make sure React re-builds it
	// each time the options change.
	const nativeSelectKey = Array.from(nativeOptionsSet)
		.map((option) => option.props.value)
		.join(";");

	return (
		<Popper {...popperScope}>
			<SelectProvider
				required={required}
				scope={__scopeSelect}
				trigger={trigger}
				onTriggerChange={setTrigger}
				valueNode={valueNode}
				onValueNodeChange={setValueNode}
				valueNodeHasChildren={valueNodeHasChildren}
				onValueNodeHasChildrenChange={setValueNodeHasChildren}
				contentId={useId()}
				value={value}
				onValueChange={setValue}
				open={open}
				onOpenChange={setOpen}
				dir={direction}
				triggerPointerDownPosRef={triggerPointerDownPosRef}
				disabled={disabled}
			>
				<Collection.Provider scope={__scopeSelect}>
					<SelectNativeOptionsProvider
						scope={props.__scopeSelect}
						onNativeOptionAdd={React.useCallback((option) => {
							setNativeOptionsSet((prev) => new Set(prev).add(option));
						}, [])}
						onNativeOptionRemove={React.useCallback((option) => {
							setNativeOptionsSet((prev) => {
								const optionsSet = new Set(prev);
								optionsSet.delete(option);
								return optionsSet;
							});
						}, [])}
					>
						{children}
					</SelectNativeOptionsProvider>
				</Collection.Provider>

				{isFormControl ? (
					<BubbleSelect
						key={nativeSelectKey}
						aria-hidden
						required={required}
						tabIndex={-1}
						name={name}
						autoComplete={autoComplete}
						value={value}
						// enable form autofill
						onChange={(event) => setValue(event.target.value)}
						disabled={disabled}
						form={form}
					>
						{value === undefined ? <option value="" /> : null}
						{Array.from(nativeOptionsSet)}
					</BubbleSelect>
				) : null}
			</SelectProvider>
		</Popper>
	);
};

Select.displayName = SELECT_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectTrigger
 * -----------------------------------------------------------------------------------------------*/

const TRIGGER_NAME = "SelectTrigger";

type SelectTriggerElement = React.ComponentRef<typeof Primitive.button>;
type PrimitiveButtonProps = React.ComponentPropsWithoutRef<
	typeof Primitive.button
>;
type SelectTriggerProps = PrimitiveButtonProps;

/**
 * Select.Trigger component for triggering the select dropdown
 *
 * This component renders the button that opens and closes the select dropdown. It typically contains the currently selected value and an icon indicating that it's a dropdown.
 */
const SelectTriggerPrimitive = React.forwardRef<
	SelectTriggerElement,
	SelectTriggerProps
>((props: ScopedProps<SelectTriggerProps>, forwardedRef) => {
	const { __scopeSelect, disabled = false, ...triggerProps } = props;
	const popperScope = usePopperScope(__scopeSelect);
	const context = useSelectContext(TRIGGER_NAME, __scopeSelect);
	const isDisabled = context.disabled || disabled;
	const composedRefs = useComposedRefs(forwardedRef, context.onTriggerChange);
	const getItems = useCollection(__scopeSelect);
	const pointerTypeRef =
		React.useRef<React.PointerEvent["pointerType"]>("touch");

	const [searchRef, handleTypeaheadSearch, resetTypeahead] = useTypeaheadSearch(
		(search) => {
			const enabledItems = getItems().filter((item) => !item.disabled);
			const currentItem = enabledItems.find(
				(item) => item.value === context.value,
			);
			const nextItem = findNextItem(enabledItems, search, currentItem);
			if (nextItem !== undefined) {
				context.onValueChange(nextItem.value);
			}
		},
	);

	const handleOpen = (pointerEvent?: React.MouseEvent | React.PointerEvent) => {
		if (!isDisabled) {
			context.onOpenChange(true);
			// reset typeahead when we open
			resetTypeahead();
		}

		if (pointerEvent) {
			context.triggerPointerDownPosRef.current = {
				x: Math.round(pointerEvent.pageX),
				y: Math.round(pointerEvent.pageY),
			};
		}
	};

	return (
		<PopperAnchor asChild {...popperScope}>
			<Primitive.button
				type="button"
				role="combobox"
				aria-controls={context.contentId}
				aria-expanded={context.open}
				aria-required={context.required}
				aria-autocomplete="none"
				dir={context.dir}
				data-state={context.open ? "open" : "closed"}
				disabled={isDisabled}
				data-disabled={isDisabled ? "" : undefined}
				data-placeholder={shouldShowPlaceholder(context.value) ? "" : undefined}
				{...triggerProps}
				ref={composedRefs}
				// Enable compatibility with native label or custom `Label` "click" for Safari:
				onClick={composeEventHandlers(triggerProps.onClick, (event) => {
					// Whilst browsers generally have no issue focusing the trigger when clicking
					// on a label, Safari seems to struggle with the fact that there's no `onClick`.
					// We force `focus` in this case. Note: this doesn't create any other side-effect
					// because we are preventing default in `onPointerDown` so effectively
					// this only runs for a label "click"
					event.currentTarget.focus();

					// Open on click when using a touch or pen device
					if (pointerTypeRef.current !== "mouse") {
						handleOpen(event);
					}
				})}
				onPointerDown={composeEventHandlers(
					triggerProps.onPointerDown,
					(event) => {
						pointerTypeRef.current = event.pointerType;

						// prevent implicit pointer capture
						// https://www.w3.org/TR/pointerevents3/#implicit-pointer-capture
						const target = event.target as HTMLElement;
						if (target.hasPointerCapture(event.pointerId)) {
							target.releasePointerCapture(event.pointerId);
						}

						// only call handler if it's the left button (mousedown gets triggered by all mouse buttons)
						// but not when the control key is pressed (avoiding MacOS right click); also not for touch
						// devices because that would open the menu on scroll. (pen devices behave as touch on iOS).
						if (
							event.button === 0 &&
							event.ctrlKey === false &&
							event.pointerType === "mouse"
						) {
							handleOpen(event);
							// prevent trigger from stealing focus from the active item after opening.
							event.preventDefault();
						}
					},
				)}
				onKeyDown={composeEventHandlers(triggerProps.onKeyDown, (event) => {
					const isTypingAhead = searchRef.current !== "";
					const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
					if (!isModifierKey && event.key.length === 1) {
						handleTypeaheadSearch(event.key);
					}
					if (isTypingAhead && event.key === " ") {
						return;
					}
					if (OPEN_KEYS.includes(event.key)) {
						handleOpen();
						event.preventDefault();
					}
				})}
			/>
		</PopperAnchor>
	);
});

SelectTriggerPrimitive.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectValue
 * -----------------------------------------------------------------------------------------------*/

const VALUE_NAME = "SelectValue";

type SelectValueElement = React.ComponentRef<typeof Primitive.span>;
type PrimitiveSpanProps = React.ComponentPropsWithoutRef<typeof Primitive.span>;
interface SelectValueProps extends Omit<PrimitiveSpanProps, "placeholder"> {
	/** The placeholder content displayed when no value is selected. */
	placeholder?: React.ReactNode;
}

/**
 * Select.Value component for displaying the selected value
 *
 * This component renders the currently selected value within the Select.Trigger component.
 */
const SelectValue = React.forwardRef<SelectValueElement, SelectValueProps>(
	(props: ScopedProps<SelectValueProps>, forwardedRef) => {
		// We ignore `className` and `style` as this part shouldn't be styled.
		const {
			__scopeSelect,
			className: _className,
			style: _style,
			children,
			placeholder = "",
			...valueProps
		} = props;
		const context = useSelectContext(VALUE_NAME, __scopeSelect);
		const { onValueNodeHasChildrenChange } = context;
		const hasChildren = children !== undefined;
		const composedRefs = useComposedRefs(
			forwardedRef,
			context.onValueNodeChange,
		);

		useLayoutEffect(() => {
			onValueNodeHasChildrenChange(hasChildren);
		}, [onValueNodeHasChildrenChange, hasChildren]);

		return (
			<Primitive.span
				{...valueProps}
				ref={composedRefs}
				// we don't want events from the portalled `SelectValue` children to bubble
				// through the item they came from
				style={{ pointerEvents: "none" }}
			>
				{shouldShowPlaceholder(context.value) ? <>{placeholder}</> : children}
			</Primitive.span>
		);
	},
);

SelectValue.displayName = VALUE_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectIcon
 * -----------------------------------------------------------------------------------------------*/

const ICON_NAME = "SelectIcon";

type SelectIconElement = React.ComponentRef<typeof Primitive.span>;
type SelectIconProps = PrimitiveSpanProps;

/**
 * Select.Icon component for the dropdown icon
 *
 * This component renders an icon (typically a chevron) within the Select.Trigger to indicate that it's a dropdown.
 */
const SelectIcon = React.forwardRef<SelectIconElement, SelectIconProps>(
	(props: ScopedProps<SelectIconProps>, forwardedRef) => {
		const { __scopeSelect, children, ...iconProps } = props;
		return (
			<Primitive.span aria-hidden {...iconProps} ref={forwardedRef}>
				{children || "▼"}
			</Primitive.span>
		);
	},
);

SelectIcon.displayName = ICON_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectPortal
 * -----------------------------------------------------------------------------------------------*/

const PORTAL_NAME = "SelectPortal";

type PortalProps = React.ComponentPropsWithoutRef<typeof PortalPrimitive>;
interface SelectPortalProps {
	children?: React.ReactNode;
	/**
	 * Specify a container element to portal the content into.
	 */
	container?: PortalProps["container"];
}

const SelectPortal: React.FC<SelectPortalProps> = (
	props: ScopedProps<SelectPortalProps>,
) => {
	return <PortalPrimitive asChild {...props} />;
};

SelectPortal.displayName = PORTAL_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectContent
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "SelectContent";

type SelectContentElement = SelectContentImplElement;
type SelectContentProps = SelectContentImplProps;

/**
 * Select.Content component for the dropdown content
 *
 * This component renders the dropdown menu containing the selectable options. It's conditionally rendered based on the open state of the Select.
 */
const SelectContentPrimitive = React.forwardRef<
	SelectContentElement,
	SelectContentProps
>((props: ScopedProps<SelectContentProps>, forwardedRef) => {
	const context = useSelectContext(CONTENT_NAME, props.__scopeSelect);
	const [fragment, setFragment] = React.useState<DocumentFragment>();

	// setting the fragment in `useLayoutEffect` as `DocumentFragment` doesn't exist on the server
	useLayoutEffect(() => {
		setFragment(new DocumentFragment());
	}, []);

	if (!context.open) {
		const frag = fragment as Element | undefined;
		return frag
			? ReactDOM.createPortal(
					<SelectContentProvider scope={props.__scopeSelect}>
						<Collection.Slot scope={props.__scopeSelect}>
							<div>{props.children}</div>
						</Collection.Slot>
					</SelectContentProvider>,
					frag,
				)
			: null;
	}

	return <SelectContentImpl {...props} ref={forwardedRef} />;
});

SelectContentPrimitive.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectContentImpl
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_MARGIN = 10;

type SelectContentContextValue = {
	/** The current content element for the select component. */
	content?: SelectContentElement | null;

	/** The current viewport element inside the select component. */
	viewport?: SelectViewportElement | null;

	/** Callback function triggered when the viewport element changes. */
	onViewportChange?: (node: SelectViewportElement | null) => void;

	/** Callback function for referencing individual items in the select dropdown. */
	itemRefCallback?: (
		node: SelectItemElement | null,
		value: string,
		_disabled: boolean,
	) => void;

	/** The currently selected item in the select dropdown. */
	selectedItem?: SelectItemElement | null;

	/** Callback function triggered when the mouse leaves a select item. */
	onItemLeave?: () => void;

	/** Callback function for referencing the text of individual select items. */
	itemTextRefCallback?: (
		_node: SelectItemTextElement | null,
		value: string,
		_disabled: boolean,
	) => void;

	/** Function to focus on the selected item in the select dropdown. */
	focusSelectedItem?: () => void;

	/** The text element of the currently selected item in the select dropdown. */
	selectedItemText?: SelectItemTextElement | null;

	/** The positioning strategy for the select content element (e.g., 'absolute', 'fixed'). */
	position?: SelectContentProps["position"];

	/** Indicates whether the select content has been positioned. */
	isPositioned?: boolean;

	/** A reference for storing search input within the select component. */
	searchRef?: React.RefObject<string>;
};

const [SelectContentProvider, useSelectContentContext] =
	createSelectContext<SelectContentContextValue>(CONTENT_NAME);

const CONTENT_IMPL_NAME = "SelectContentImpl";

type SelectContentImplElement =
	| SelectPopperPositionElement
	| SelectItemAlignedPositionElement;
type DismissableLayerProps = React.ComponentPropsWithoutRef<
	typeof DismissableLayer
>;
type FocusScopeProps = React.ComponentPropsWithoutRef<typeof FocusScope>;

type SelectPopperPrivateProps = { onPlaced?: PopperContentProps["onPlaced"] };

interface SelectContentImplProps
	extends Omit<SelectPopperPositionProps, keyof SelectPopperPrivateProps>,
		Omit<SelectItemAlignedPositionProps, keyof SelectPopperPrivateProps> {
	/**
	 * Event handler called when auto-focusing on close.
	 * Can be prevented.
	 */
	onCloseAutoFocus?: FocusScopeProps["onUnmountAutoFocus"];
	/**
	 * Event handler called when the escape key is down.
	 * Can be prevented.
	 */
	onEscapeKeyDown?: DismissableLayerProps["onEscapeKeyDown"];
	/**
	 * Event handler called when the a `pointerdown` event happens outside of the `DismissableLayer`.
	 * Can be prevented.
	 */
	onPointerDownOutside?: DismissableLayerProps["onPointerDownOutside"];

	position?: "item-aligned" | "popper";
}

const SelectContentImpl = React.forwardRef<
	SelectContentImplElement,
	SelectContentImplProps
>((props: ScopedProps<SelectContentImplProps>, forwardedRef) => {
	const {
		__scopeSelect,
		position = "item-aligned",
		onCloseAutoFocus,
		onEscapeKeyDown,
		onPointerDownOutside,
		//
		// PopperContent props
		side,
		sideOffset,
		align,
		alignOffset,
		arrowPadding,
		collisionBoundary,
		collisionPadding,
		sticky,
		hideWhenDetached,
		avoidCollisions,
		//
		...contentProps
	} = props;
	const context = useSelectContext(CONTENT_NAME, __scopeSelect);
	const [content, setContent] = React.useState<SelectContentImplElement | null>(
		null,
	);
	const [viewport, setViewport] = React.useState<SelectViewportElement | null>(
		null,
	);
	const composedRefs = useComposedRefs(forwardedRef, (node) =>
		setContent(node),
	);
	const [selectedItem, setSelectedItem] =
		React.useState<SelectItemElement | null>(null);
	const [selectedItemText, setSelectedItemText] =
		React.useState<SelectItemTextElement | null>(null);
	const getItems = useCollection(__scopeSelect);
	const [isPositioned, setIsPositioned] = React.useState(false);
	const firstValidItemFoundRef = React.useRef(false);

	// aria-hide everything except the content (better supported equivalent to setting aria-modal)
	React.useEffect(() => {
		if (content) {
			return hideOthers(content);
		}
	}, [content]);

	// Make sure the whole tree has focus guards as our `Select` may be
	// the last element in the DOM (because of the `Portal`)
	useFocusGuards();

	const focusFirst = React.useCallback(
		(candidates: Array<HTMLElement | null>) => {
			const [firstItem, ...restItems] = getItems().map(
				(item) => item.ref.current,
			);
			const [lastItem] = restItems.slice(-1);

			const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
			for (const candidate of candidates) {
				// if focus is already where we want to go, we don't want to keep going through the candidates
				if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) {
					return;
				}
				candidate?.scrollIntoView({ block: "nearest" });
				// viewport might have padding so scroll to its edges when focusing first/last items.
				if (candidate === firstItem && viewport) {
					viewport.scrollTop = 0;
				}
				if (candidate === lastItem && viewport) {
					viewport.scrollTop = viewport.scrollHeight;
				}
				candidate?.focus();
				if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) {
					return;
				}
			}
		},
		[getItems, viewport],
	);

	const focusSelectedItem = React.useCallback(
		() => focusFirst([selectedItem, content]),
		[focusFirst, selectedItem, content],
	);

	// Since this is not dependent on layout, we want to ensure this runs at the same time as
	// other effects across components. Hence why we don't call `focusSelectedItem` inside `position`.
	React.useEffect(() => {
		if (isPositioned) {
			focusSelectedItem();
		}
	}, [isPositioned, focusSelectedItem]);

	// prevent selecting items on `pointerup` in some cases after opening from `pointerdown`
	// and close on `pointerup` outside.
	const { onOpenChange, triggerPointerDownPosRef } = context;
	React.useEffect(() => {
		if (content) {
			let pointerMoveDelta = { x: 0, y: 0 };

			const handlePointerMove = (event: PointerEvent) => {
				pointerMoveDelta = {
					x: Math.abs(
						Math.round(event.pageX) -
							(triggerPointerDownPosRef.current?.x ?? 0),
					),
					y: Math.abs(
						Math.round(event.pageY) -
							(triggerPointerDownPosRef.current?.y ?? 0),
					),
				};
			};
			const handlePointerUp = (event: PointerEvent) => {
				// If the pointer hasn't moved by a certain threshold then we prevent selecting item on `pointerup`.
				if (pointerMoveDelta.x <= 10 && pointerMoveDelta.y <= 10) {
					event.preventDefault();
				} else {
					// otherwise, if the event was outside the content, close.
					if (!content.contains(event.target as HTMLElement)) {
						onOpenChange(false);
					}
				}
				document.removeEventListener("pointermove", handlePointerMove);
				triggerPointerDownPosRef.current = null;
			};

			if (triggerPointerDownPosRef.current !== null) {
				document.addEventListener("pointermove", handlePointerMove);
				document.addEventListener("pointerup", handlePointerUp, {
					capture: true,
					once: true,
				});
			}

			return () => {
				document.removeEventListener("pointermove", handlePointerMove);
				document.removeEventListener("pointerup", handlePointerUp, {
					capture: true,
				});
			};
		}
	}, [content, onOpenChange, triggerPointerDownPosRef]);

	React.useEffect(() => {
		const close = () => onOpenChange(false);
		window.addEventListener("blur-sm", close);
		window.addEventListener("resize", close);
		return () => {
			window.removeEventListener("blur-sm", close);
			window.removeEventListener("resize", close);
		};
	}, [onOpenChange]);

	const [searchRef, handleTypeaheadSearch] = useTypeaheadSearch((search) => {
		const enabledItems = getItems().filter((item) => !item.disabled);
		const currentItem = enabledItems.find(
			(item) => item.ref.current === document.activeElement,
		);
		const nextItem = findNextItem(enabledItems, search, currentItem);
		if (nextItem) {
			/**
			 * Imperative focus during keydown is risky so we prevent React's batching updates
			 * to avoid potential bugs. See: https://github.com/facebook/react/issues/20332
			 */
			setTimeout(() => (nextItem.ref.current as HTMLElement).focus());
		}
	});

	const itemRefCallback = React.useCallback(
		(node: SelectItemElement | null, value: string, disabled: boolean) => {
			const isFirstValidItem = !firstValidItemFoundRef.current && !disabled;
			const isSelectedItem =
				context.value !== undefined && context.value === value;
			if (isSelectedItem || isFirstValidItem) {
				setSelectedItem(node);
				if (isFirstValidItem) {
					firstValidItemFoundRef.current = true;
				}
			}
		},
		[context.value],
	);
	const handleItemLeave = React.useCallback(() => content?.focus(), [content]);
	const itemTextRefCallback = React.useCallback(
		(node: SelectItemTextElement | null, value: string, disabled: boolean) => {
			const isFirstValidItem = !firstValidItemFoundRef.current && !disabled;
			const isSelectedItem =
				context.value !== undefined && context.value === value;
			if (isSelectedItem || isFirstValidItem) {
				setSelectedItemText(node);
			}
		},
		[context.value],
	);

	const SelectPosition =
		position === "popper" ? SelectPopperPosition : SelectItemAlignedPosition;

	// Silently ignore props that are not supported by `SelectItemAlignedPosition`
	const popperContentProps =
		SelectPosition === SelectPopperPosition
			? {
					side,
					sideOffset,
					align,
					alignOffset,
					arrowPadding,
					collisionBoundary,
					collisionPadding,
					sticky,
					hideWhenDetached,
					avoidCollisions,
				}
			: {};

	return (
		<SelectContentProvider
			scope={__scopeSelect}
			content={content}
			viewport={viewport}
			onViewportChange={setViewport}
			itemRefCallback={itemRefCallback}
			selectedItem={selectedItem}
			onItemLeave={handleItemLeave}
			itemTextRefCallback={itemTextRefCallback}
			focusSelectedItem={focusSelectedItem}
			selectedItemText={selectedItemText}
			position={position}
			isPositioned={isPositioned}
			searchRef={searchRef}
		>
			<RemoveScroll as={Slot} allowPinchZoom>
				<FocusScope
					asChild
					// we make sure we're not trapping once it's been closed
					// (closed !== unmounted when animating out)
					trapped={context.open}
					onMountAutoFocus={(event) => {
						// we prevent open autofocus because we manually focus the selected item
						event.preventDefault();
					}}
					onUnmountAutoFocus={composeEventHandlers(
						onCloseAutoFocus,
						(event) => {
							context.trigger?.focus({ preventScroll: true });
							event.preventDefault();
						},
					)}
				>
					<DismissableLayer
						asChild
						disableOutsidePointerEvents
						onEscapeKeyDown={onEscapeKeyDown}
						onPointerDownOutside={onPointerDownOutside}
						// When focus is trapped, a focusout event may still happen.
						// We make sure we don't trigger our `onDismiss` in such case.
						onFocusOutside={(event) => event.preventDefault()}
						onDismiss={() => context.onOpenChange(false)}
					>
						<SelectPosition
							role="listbox"
							id={context.contentId}
							data-state={context.open ? "open" : "closed"}
							dir={context.dir}
							onContextMenu={(event) => event.preventDefault()}
							{...contentProps}
							{...popperContentProps}
							onPlaced={() => setIsPositioned(true)}
							ref={composedRefs}
							style={{
								// flex layout so we can place the scroll buttons properly
								display: "flex",
								flexDirection: "column",
								// reset the outline by default as the content MAY get focused
								outline: "none",
								...contentProps.style,
							}}
							onKeyDown={composeEventHandlers(
								contentProps.onKeyDown,
								(event) => {
									const isModifierKey =
										event.ctrlKey || event.altKey || event.metaKey;

									// select should not be navigated using tab key so we prevent it
									if (event.key === "Tab") {
										event.preventDefault();
									}

									if (!isModifierKey && event.key.length === 1) {
										handleTypeaheadSearch(event.key);
									}

									if (
										["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)
									) {
										const items = getItems().filter((item) => !item.disabled);

										let candidateNodes = items.map((item) => item.ref.current!);

										if (["ArrowUp", "End"].includes(event.key)) {
											candidateNodes = candidateNodes.slice().reverse();
										}
										if (["ArrowUp", "ArrowDown"].includes(event.key)) {
											const currentElement = event.target as SelectItemElement;
											const currentIndex =
												candidateNodes.indexOf(currentElement);
											candidateNodes = candidateNodes.slice(currentIndex + 1);
										}

										/**
										 * Imperative focus during keydown is risky so we prevent React's batching updates
										 * to avoid potential bugs. See: https://github.com/facebook/react/issues/20332
										 */
										setTimeout(() => focusFirst(candidateNodes));

										event.preventDefault();
									}
								},
							)}
						/>
					</DismissableLayer>
				</FocusScope>
			</RemoveScroll>
		</SelectContentProvider>
	);
});

SelectContentImpl.displayName = CONTENT_IMPL_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectItemAlignedPosition
 * -----------------------------------------------------------------------------------------------*/

const ITEM_ALIGNED_POSITION_NAME = "SelectItemAlignedPosition";

type SelectItemAlignedPositionElement = React.ComponentRef<
	typeof Primitive.div
>;
interface SelectItemAlignedPositionProps
	extends PrimitiveDivProps,
		SelectPopperPrivateProps {}

const SelectItemAlignedPosition = React.forwardRef<
	SelectItemAlignedPositionElement,
	SelectItemAlignedPositionProps
>((props: ScopedProps<SelectItemAlignedPositionProps>, forwardedRef) => {
	const { __scopeSelect, onPlaced, ...popperProps } = props;
	const context = useSelectContext(CONTENT_NAME, __scopeSelect);
	const contentContext = useSelectContentContext(CONTENT_NAME, __scopeSelect);
	const [contentWrapper, setContentWrapper] =
		React.useState<HTMLDivElement | null>(null);
	const [content, setContent] =
		React.useState<SelectItemAlignedPositionElement | null>(null);
	const composedRefs = useComposedRefs(forwardedRef, (node) =>
		setContent(node),
	);
	const getItems = useCollection(__scopeSelect);
	const shouldExpandOnScrollRef = React.useRef(false);
	const shouldRepositionRef = React.useRef(true);

	const { viewport, selectedItem, selectedItemText, focusSelectedItem } =
		contentContext;
	const position = React.useCallback(() => {
		if (
			context.trigger &&
			context.valueNode &&
			contentWrapper &&
			content &&
			viewport &&
			selectedItem &&
			selectedItemText
		) {
			const triggerRect = context.trigger.getBoundingClientRect();

			// -----------------------------------------------------------------------------------------
			//  Horizontal positioning
			// -----------------------------------------------------------------------------------------
			const contentRect = content.getBoundingClientRect();
			const valueNodeRect = context.valueNode.getBoundingClientRect();
			const itemTextRect = selectedItemText.getBoundingClientRect();

			if (context.dir !== "rtl") {
				const itemTextOffset = itemTextRect.left - contentRect.left;
				const left = valueNodeRect.left - itemTextOffset;
				const leftDelta = triggerRect.left - left;
				const minContentWidth = triggerRect.width + leftDelta;
				const contentWidth = Math.max(minContentWidth, contentRect.width);
				const rightEdge = window.innerWidth - CONTENT_MARGIN;
				const clampedLeft = clamp(left, [
					CONTENT_MARGIN,
					// Prevents the content from going off the starting edge of the
					// viewport. It may still go off the ending edge, but this can be
					// controlled by the user since they may want to manage overflow in a
					// specific way.
					Math.max(CONTENT_MARGIN, rightEdge - contentWidth),
				]);

				contentWrapper.style.minWidth = `${minContentWidth}px`;
				contentWrapper.style.left = `${clampedLeft}px`;
			} else {
				const itemTextOffset = contentRect.right - itemTextRect.right;
				const right = window.innerWidth - valueNodeRect.right - itemTextOffset;
				const rightDelta = window.innerWidth - triggerRect.right - right;
				const minContentWidth = triggerRect.width + rightDelta;
				const contentWidth = Math.max(minContentWidth, contentRect.width);
				const leftEdge = window.innerWidth - CONTENT_MARGIN;
				const clampedRight = clamp(right, [
					CONTENT_MARGIN,
					Math.max(CONTENT_MARGIN, leftEdge - contentWidth),
				]);

				contentWrapper.style.minWidth = `${minContentWidth}px`;
				contentWrapper.style.right = `${clampedRight}px`;
			}

			// -----------------------------------------------------------------------------------------
			// Vertical positioning
			// -----------------------------------------------------------------------------------------
			const items = getItems();
			const availableHeight = window.innerHeight - CONTENT_MARGIN * 2;
			const itemsHeight = viewport.scrollHeight;

			const contentStyles = window.getComputedStyle(content);
			const contentBorderTopWidth = Number.parseInt(
				contentStyles.borderTopWidth,
				10,
			);
			const contentPaddingTop = Number.parseInt(contentStyles.paddingTop, 10);
			const contentBorderBottomWidth = Number.parseInt(
				contentStyles.borderBottomWidth,
				10,
			);
			const contentPaddingBottom = Number.parseInt(
				contentStyles.paddingBottom,
				10,
			);
			const fullContentHeight =
				contentBorderTopWidth +
				contentPaddingTop +
				itemsHeight +
				contentPaddingBottom +
				contentBorderBottomWidth; // prettier-ignore
			const minContentHeight = Math.min(
				selectedItem.offsetHeight * 5,
				fullContentHeight,
			);

			const viewportStyles = window.getComputedStyle(viewport);
			const viewportPaddingTop = Number.parseInt(viewportStyles.paddingTop, 10);
			const viewportPaddingBottom = Number.parseInt(
				viewportStyles.paddingBottom,
				10,
			);

			const topEdgeToTriggerMiddle =
				triggerRect.top + triggerRect.height / 2 - CONTENT_MARGIN;
			const triggerMiddleToBottomEdge =
				availableHeight - topEdgeToTriggerMiddle;

			const selectedItemHalfHeight = selectedItem.offsetHeight / 2;
			const itemOffsetMiddle = selectedItem.offsetTop + selectedItemHalfHeight;
			const contentTopToItemMiddle =
				contentBorderTopWidth + contentPaddingTop + itemOffsetMiddle;
			const itemMiddleToContentBottom =
				fullContentHeight - contentTopToItemMiddle;

			const willAlignWithoutTopOverflow =
				contentTopToItemMiddle <= topEdgeToTriggerMiddle;

			if (willAlignWithoutTopOverflow) {
				const isLastItem =
					items.length > 0 &&
					selectedItem === items[items.length - 1].ref.current;
				contentWrapper.style.bottom = `${0}px`;
				const viewportOffsetBottom =
					content.clientHeight - viewport.offsetTop - viewport.offsetHeight;
				const clampedTriggerMiddleToBottomEdge = Math.max(
					triggerMiddleToBottomEdge,
					selectedItemHalfHeight +
						// viewport might have padding bottom, include it to avoid a scrollable viewport
						(isLastItem ? viewportPaddingBottom : 0) +
						viewportOffsetBottom +
						contentBorderBottomWidth,
				);
				const height =
					contentTopToItemMiddle + clampedTriggerMiddleToBottomEdge;
				contentWrapper.style.height = `${height}px`;
			} else {
				const isFirstItem =
					items.length > 0 && selectedItem === items[0].ref.current;
				contentWrapper.style.top = `${0}px`;
				const clampedTopEdgeToTriggerMiddle = Math.max(
					topEdgeToTriggerMiddle,
					contentBorderTopWidth +
						viewport.offsetTop +
						// viewport might have padding top, include it to avoid a scrollable viewport
						(isFirstItem ? viewportPaddingTop : 0) +
						selectedItemHalfHeight,
				);
				const height =
					clampedTopEdgeToTriggerMiddle + itemMiddleToContentBottom;
				contentWrapper.style.height = `${height}px`;
				viewport.scrollTop =
					contentTopToItemMiddle - topEdgeToTriggerMiddle + viewport.offsetTop;
			}

			contentWrapper.style.margin = `${CONTENT_MARGIN}px 0`;
			contentWrapper.style.minHeight = `${minContentHeight}px`;
			contentWrapper.style.maxHeight = `${availableHeight}px`;
			// -----------------------------------------------------------------------------------------

			onPlaced?.();

			// we don't want the initial scroll position adjustment to trigger "expand on scroll"
			// so we explicitly turn it on only after they've registered.
			// biome-ignore lint/suspicious/noAssignInExpressions: This is a workaround for a bug in React
			requestAnimationFrame(() => (shouldExpandOnScrollRef.current = true));
		}
	}, [
		getItems,
		context.trigger,
		context.valueNode,
		contentWrapper,
		content,
		viewport,
		selectedItem,
		selectedItemText,
		context.dir,
		onPlaced,
	]);

	useLayoutEffect(() => position(), [position]);

	// copy z-index from content to wrapper
	const [contentZIndex, setContentZIndex] = React.useState<string>();
	useLayoutEffect(() => {
		if (content) {
			setContentZIndex(window.getComputedStyle(content).zIndex);
		}
	}, [content]);

	// When the viewport becomes scrollable at the top, the scroll up button will mount.
	// Because it is part of the normal flow, it will push down the viewport, thus throwing our
	// trigger => selectedItem alignment off by the amount the viewport was pushed down.
	// We wait for this to happen and then re-run the positining logic one more time to account for it.
	const handleScrollButtonChange = React.useCallback(
		(node: SelectScrollButtonImplElement | null) => {
			if (node && shouldRepositionRef.current === true) {
				position();
				focusSelectedItem?.();
				shouldRepositionRef.current = false;
			}
		},
		[position, focusSelectedItem],
	);

	return (
		<SelectViewportProvider
			scope={__scopeSelect}
			contentWrapper={contentWrapper}
			shouldExpandOnScrollRef={shouldExpandOnScrollRef}
			onScrollButtonChange={handleScrollButtonChange}
		>
			<div
				ref={setContentWrapper}
				style={{
					display: "flex",
					flexDirection: "column",
					position: "fixed",
					zIndex: contentZIndex,
				}}
			>
				<Primitive.div
					{...popperProps}
					ref={composedRefs}
					style={{
						// When we get the height of the content, it includes borders. If we were to set
						// the height without having `boxSizing: 'border-box'` it would be too big.
						boxSizing: "border-box",
						// We need to ensure the content doesn't get taller than the wrapper
						maxHeight: "100%",
						...popperProps.style,
					}}
				/>
			</div>
		</SelectViewportProvider>
	);
});

SelectItemAlignedPosition.displayName = ITEM_ALIGNED_POSITION_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectPopperPosition
 * -----------------------------------------------------------------------------------------------*/

const POPPER_POSITION_NAME = "SelectPopperPosition";

type SelectPopperPositionElement = React.ComponentRef<typeof PopperContent>;
type PopperContentProps = React.ComponentPropsWithoutRef<typeof PopperContent>;
interface SelectPopperPositionProps
	extends PopperContentProps,
		SelectPopperPrivateProps {}

const SelectPopperPosition = React.forwardRef<
	SelectPopperPositionElement,
	SelectPopperPositionProps
>((props: ScopedProps<SelectPopperPositionProps>, forwardedRef) => {
	const {
		__scopeSelect,
		align = "start",
		collisionPadding = CONTENT_MARGIN,
		...popperProps
	} = props;
	const popperScope = usePopperScope(__scopeSelect);

	return (
		<PopperContent
			{...popperScope}
			{...popperProps}
			ref={forwardedRef}
			align={align}
			collisionPadding={collisionPadding}
			style={{
				// Ensure border-box for floating-ui calculations
				boxSizing: "border-box",
				...popperProps.style,
				// re-namespace exposed content custom properties
				...{
					"--squared-select-content-transform-origin":
						"var(--squared-popper-transform-origin)",
					"--squared-select-content-available-width":
						"var(--squared-popper-available-width)",
					"--squared-select-content-available-height":
						"var(--squared-popper-available-height)",
					"--squared-select-trigger-width":
						"var(--squared-popper-anchor-width)",
					"--squared-select-trigger-height":
						"var(--squared-popper-anchor-height)",
				},
			}}
		/>
	);
});

SelectPopperPosition.displayName = POPPER_POSITION_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectViewport
 * -----------------------------------------------------------------------------------------------*/

type SelectViewportContextValue = {
	contentWrapper?: HTMLDivElement | null;
	shouldExpandOnScrollRef?: React.RefObject<boolean>;
	onScrollButtonChange?: (node: SelectScrollButtonImplElement | null) => void;
};

const [SelectViewportProvider, useSelectViewportContext] =
	createSelectContext<SelectViewportContextValue>(CONTENT_NAME, {});

const VIEWPORT_NAME = "SelectViewport";

type SelectViewportElement = React.ComponentRef<typeof Primitive.div>;
type PrimitiveDivProps = React.ComponentPropsWithoutRef<typeof Primitive.div>;
interface SelectViewportProps extends PrimitiveDivProps {
	/** A cryptographic nonce used for Content Security Policy (CSP) to allow inline styles or scripts. */
	nonce?: string;
}

/**
 * SelectViewport component for the scrollable viewport of the dropdown
 *
 * This component provides a scrollable container for the dropdown options, useful when there are many options to choose from.
 */
const SelectViewport = React.forwardRef<
	SelectViewportElement,
	SelectViewportProps
>((props: ScopedProps<SelectViewportProps>, forwardedRef) => {
	const { __scopeSelect, nonce, ...viewportProps } = props;
	const contentContext = useSelectContentContext(VIEWPORT_NAME, __scopeSelect);
	const viewportContext = useSelectViewportContext(
		VIEWPORT_NAME,
		__scopeSelect,
	);
	const composedRefs = useComposedRefs(
		forwardedRef,
		contentContext.onViewportChange,
	);
	const prevScrollTopRef = React.useRef(0);
	return (
		<>
			{/* Hide scrollbars cross-browser and enable momentum scroll for touch devices */}
			<style
				// biome-ignore lint/security/noDangerouslySetInnerHtml: This is a workaround for a bug in React
				dangerouslySetInnerHTML={{
					__html:
						"[data-squared-select-viewport]{scrollbar-width:none;-ms-overflow-style:none;-webkit-overflow-scrolling:touch;}[data-squared-select-viewport]::-webkit-scrollbar{display:none}",
				}}
				nonce={nonce}
			/>
			<Collection.Slot scope={__scopeSelect}>
				<Primitive.div
					data-squared-select-viewport=""
					role="presentation"
					{...viewportProps}
					ref={composedRefs}
					style={{
						// we use position: 'relative' here on the `viewport` so that when we call
						// `selectedItem.offsetTop` in calculations, the offset is relative to the viewport
						// (independent of the scrollUpButton).
						position: "relative",
						flex: 1,
						// Viewport should only be scrollable in the vertical direction.
						// This won't work in vertical writing modes, so we'll need to
						// revisit this if/when that is supported
						// https://developer.chrome.com/blog/vertical-form-controls
						overflow: "hidden auto",
						...viewportProps.style,
					}}
					onScroll={composeEventHandlers(viewportProps.onScroll, (event) => {
						const viewport = event.currentTarget;
						const { contentWrapper, shouldExpandOnScrollRef } = viewportContext;
						if (shouldExpandOnScrollRef?.current && contentWrapper) {
							const scrolledBy = Math.abs(
								prevScrollTopRef.current - viewport.scrollTop,
							);
							if (scrolledBy > 0) {
								const availableHeight = window.innerHeight - CONTENT_MARGIN * 2;
								const cssMinHeight = Number.parseFloat(
									contentWrapper.style.minHeight,
								);
								const cssHeight = Number.parseFloat(
									contentWrapper.style.height,
								);
								const prevHeight = Math.max(cssMinHeight, cssHeight);

								if (prevHeight < availableHeight) {
									const nextHeight = prevHeight + scrolledBy;
									const clampedNextHeight = Math.min(
										availableHeight,
										nextHeight,
									);
									const heightDiff = nextHeight - clampedNextHeight;

									contentWrapper.style.height = `${clampedNextHeight}px`;
									if (contentWrapper.style.bottom === "0px") {
										viewport.scrollTop = heightDiff > 0 ? heightDiff : 0;
										// ensure the content stays pinned to the bottom
										contentWrapper.style.justifyContent = "flex-end";
									}
								}
							}
						}
						prevScrollTopRef.current = viewport.scrollTop;
					})}
				/>
			</Collection.Slot>
		</>
	);
});

SelectViewport.displayName = VIEWPORT_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectGroup
 * -----------------------------------------------------------------------------------------------*/

const GROUP_NAME = "SelectGroup";

type SelectGroupContextValue = { id: string };

const [SelectGroupContextProvider, useSelectGroupContext] =
	createSelectContext<SelectGroupContextValue>(GROUP_NAME);

type SelectGroupElement = React.ComponentRef<typeof Primitive.div>;
type SelectGroupProps = PrimitiveDivProps;

/**
 * SelectGroup component for grouping select items
 *
 * This component is used to group related select options together, providing a logical separation within the dropdown.
 */
const SelectGroup = React.forwardRef<SelectGroupElement, SelectGroupProps>(
	(props: ScopedProps<SelectGroupProps>, forwardedRef) => {
		const { __scopeSelect, ...groupProps } = props;
		const groupId = useId();
		return (
			<SelectGroupContextProvider scope={__scopeSelect} id={groupId}>
				<Primitive.div
					role="group"
					aria-labelledby={groupId}
					{...groupProps}
					ref={forwardedRef}
				/>
			</SelectGroupContextProvider>
		);
	},
);

SelectGroup.displayName = GROUP_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectLabel
 * -----------------------------------------------------------------------------------------------*/

const LABEL_NAME = "SelectLabel";

type SelectLabelElement = React.ComponentRef<typeof Primitive.div>;
type SelectLabelProps = PrimitiveDivProps;

/**
 * Select.Label component for labeling groups of select items
 *
 * This component is used to provide a label or header for a group of related select options, improving the organization and readability of the dropdown.
 */
const SelectLabelPrimitive = React.forwardRef<
	SelectLabelElement,
	SelectLabelProps
>((props: ScopedProps<SelectLabelProps>, forwardedRef) => {
	const { __scopeSelect, ...labelProps } = props;
	const groupContext = useSelectGroupContext(LABEL_NAME, __scopeSelect);
	return (
		<Primitive.div id={groupContext.id} {...labelProps} ref={forwardedRef} />
	);
});

SelectLabelPrimitive.displayName = LABEL_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectItem
 * -----------------------------------------------------------------------------------------------*/

const ITEM_NAME = "SelectItem";

type SelectItemContextValue = {
	/** The value of the select item. */
	value: string;

	/** Indicates whether the select item is disabled. */
	disabled: boolean;

	/** The unique identifier for the select item's text element. */
	textId: string;

	/** Indicates whether the select item is currently selected. */
	isSelected: boolean;

	/** Callback function triggered when the select item's text changes. */
	onItemTextChange(node: SelectItemTextElement | null): void;
};

const [SelectItemContextProvider, useSelectItemContext] =
	createSelectContext<SelectItemContextValue>(ITEM_NAME);

type SelectItemElement = React.ComponentRef<typeof Primitive.div>;
interface SelectItemProps extends PrimitiveDivProps {
	/** The value of the select item. */
	value: string;

	/** Indicates whether the select item is disabled. */
	disabled?: boolean;

	/** The text value of the select item, used for accessibility and searching. */
	textValue?: string;
}

/**
 * SelectItem component for individual items in the select dropdown
 *
 * This component renders a single selectable option within the dropdown. It handles the selection state and provides visual feedback for hover and selection.
 */
const SelectItemPrimitive = React.forwardRef<
	SelectItemElement,
	SelectItemProps
>((props: ScopedProps<SelectItemProps>, forwardedRef) => {
	const {
		__scopeSelect,
		value,
		disabled = false,
		textValue: textValueProp,
		...itemProps
	} = props;
	const context = useSelectContext(ITEM_NAME, __scopeSelect);
	const contentContext = useSelectContentContext(ITEM_NAME, __scopeSelect);
	const isSelected = context.value === value;
	const [textValue, setTextValue] = React.useState(textValueProp ?? "");
	const [isFocused, setIsFocused] = React.useState(false);
	const composedRefs = useComposedRefs(forwardedRef, (node) =>
		contentContext.itemRefCallback?.(node, value, disabled),
	);
	const textId = useId();
	const pointerTypeRef =
		React.useRef<React.PointerEvent["pointerType"]>("touch");

	const handleSelect = () => {
		if (!disabled) {
			context.onValueChange(value);
			context.onOpenChange(false);
		}
	};

	if (value === "") {
		throw new Error(
			"A <SelectItem /> must have a value prop that is not an empty string. This is because the Select value can be set to an empty string to clear the selection and show the placeholder.",
		);
	}

	return (
		<SelectItemContextProvider
			scope={__scopeSelect}
			value={value}
			disabled={disabled}
			textId={textId}
			isSelected={isSelected}
			onItemTextChange={React.useCallback((node) => {
				setTextValue(
					(prevTextValue) => prevTextValue || (node?.textContent ?? "").trim(),
				);
			}, [])}
		>
			<Collection.ItemSlot
				scope={__scopeSelect}
				value={value}
				disabled={disabled}
				textValue={textValue}
			>
				<Primitive.div
					role="option"
					aria-labelledby={textId}
					data-highlighted={isFocused ? "" : undefined}
					// `isFocused` caveat fixes stuttering in VoiceOver
					aria-selected={isSelected && isFocused}
					data-state={isSelected ? "checked" : "unchecked"}
					aria-disabled={disabled || undefined}
					data-disabled={disabled ? "" : undefined}
					tabIndex={disabled ? undefined : -1}
					{...itemProps}
					ref={composedRefs}
					onFocus={composeEventHandlers(itemProps.onFocus, () =>
						setIsFocused(true),
					)}
					onBlur={composeEventHandlers(itemProps.onBlur, () =>
						setIsFocused(false),
					)}
					onClick={composeEventHandlers(itemProps.onClick, () => {
						// Open on click when using a touch or pen device
						if (pointerTypeRef.current !== "mouse") {
							handleSelect();
						}
					})}
					onPointerUp={composeEventHandlers(itemProps.onPointerUp, () => {
						// Using a mouse you should be able to do pointer down, move through
						// the list, and release the pointer over the item to select it.
						if (pointerTypeRef.current === "mouse") {
							handleSelect();
						}
					})}
					onPointerDown={composeEventHandlers(
						itemProps.onPointerDown,
						(event) => {
							pointerTypeRef.current = event.pointerType;
						},
					)}
					onPointerMove={composeEventHandlers(
						itemProps.onPointerMove,
						(event) => {
							// Remember pointer type when sliding over to this item from another one
							pointerTypeRef.current = event.pointerType;
							if (disabled) {
								contentContext.onItemLeave?.();
							} else if (pointerTypeRef.current === "mouse") {
								// even though safari doesn't support this option, it's acceptable
								// as it only means it might scroll a few pixels when using the pointer.
								event.currentTarget.focus({ preventScroll: true });
							}
						},
					)}
					onPointerLeave={composeEventHandlers(
						itemProps.onPointerLeave,
						(event) => {
							if (event.currentTarget === document.activeElement) {
								contentContext.onItemLeave?.();
							}
						},
					)}
					onKeyDown={composeEventHandlers(itemProps.onKeyDown, (event) => {
						const isTypingAhead = contentContext.searchRef?.current !== "";
						if (isTypingAhead && event.key === " ") {
							return;
						}
						if (SELECTION_KEYS.includes(event.key)) {
							handleSelect();
						}
						// prevent page scroll if using the space key to select an item
						if (event.key === " ") {
							event.preventDefault();
						}
					})}
				/>
			</Collection.ItemSlot>
		</SelectItemContextProvider>
	);
});

SelectItemPrimitive.displayName = ITEM_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectItemText
 * -----------------------------------------------------------------------------------------------*/

const ITEM_TEXT_NAME = "SelectItemText";

type SelectItemTextElement = React.ComponentRef<typeof Primitive.span>;
type SelectItemTextProps = PrimitiveSpanProps;

/**
 * SelectItemText component for the text content of a select item
 *
 * This component renders the text label for a select option.
 */
const SelectItemText = React.forwardRef<
	SelectItemTextElement,
	SelectItemTextProps
>((props: ScopedProps<SelectItemTextProps>, forwardedRef) => {
	// We ignore `className` and `style` as this part shouldn't be styled.
	const {
		__scopeSelect,
		className: _className,
		style: _style,
		...itemTextProps
	} = props;
	const context = useSelectContext(ITEM_TEXT_NAME, __scopeSelect);
	const contentContext = useSelectContentContext(ITEM_TEXT_NAME, __scopeSelect);
	const itemContext = useSelectItemContext(ITEM_TEXT_NAME, __scopeSelect);
	const nativeOptionsContext = useSelectNativeOptionsContext(
		ITEM_TEXT_NAME,
		__scopeSelect,
	);
	const [itemTextNode, setItemTextNode] =
		React.useState<SelectItemTextElement | null>(null);
	const composedRefs = useComposedRefs(
		forwardedRef,
		(node) => setItemTextNode(node),
		itemContext.onItemTextChange,
		(node) =>
			contentContext.itemTextRefCallback?.(
				node,
				itemContext.value,
				itemContext.disabled,
			),
	);

	const textContent = itemTextNode?.textContent;
	const nativeOption = React.useMemo(
		() => (
			<option
				key={itemContext.value}
				value={itemContext.value}
				disabled={itemContext.disabled}
			>
				{textContent}
			</option>
		),
		[itemContext.disabled, itemContext.value, textContent],
	);

	const { onNativeOptionAdd, onNativeOptionRemove } = nativeOptionsContext;
	useLayoutEffect(() => {
		onNativeOptionAdd(nativeOption);
		return () => onNativeOptionRemove(nativeOption);
	}, [onNativeOptionAdd, onNativeOptionRemove, nativeOption]);

	return (
		<>
			<Primitive.span
				id={itemContext.textId}
				{...itemTextProps}
				ref={composedRefs}
			/>

			{/* Portal the select item text into the trigger value node */}
			{itemContext.isSelected &&
			context.valueNode &&
			!context.valueNodeHasChildren
				? ReactDOM.createPortal(itemTextProps.children, context.valueNode)
				: null}
		</>
	);
});

SelectItemText.displayName = ITEM_TEXT_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectItemIndicator
 * -----------------------------------------------------------------------------------------------*/

const ITEM_INDICATOR_NAME = "SelectItemIndicator";

type SelectItemIndicatorElement = React.ComponentRef<typeof Primitive.span>;
type SelectItemIndicatorProps = PrimitiveSpanProps;

/**
 * SelectItemIndicator component for indicating the selected item
 *
 * This component renders a checkmark or other indicator for the currently selected option.
 */
const SelectItemIndicator = React.forwardRef<
	SelectItemIndicatorElement,
	SelectItemIndicatorProps
>((props: ScopedProps<SelectItemIndicatorProps>, forwardedRef) => {
	const { __scopeSelect, ...itemIndicatorProps } = props;
	const itemContext = useSelectItemContext(ITEM_INDICATOR_NAME, __scopeSelect);
	return itemContext.isSelected ? (
		<Primitive.span aria-hidden {...itemIndicatorProps} ref={forwardedRef} />
	) : null;
});

SelectItemIndicator.displayName = ITEM_INDICATOR_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectScrollUpButton
 * -----------------------------------------------------------------------------------------------*/

const SCROLL_UP_BUTTON_NAME = "SelectScrollUpButton";

type SelectScrollUpButtonElement = SelectScrollButtonImplElement;
type SelectScrollUpButtonProps = Omit<
	SelectScrollButtonImplProps,
	"onAutoScroll"
>;

const SelectScrollUpButtonPrimitive = React.forwardRef<
	SelectScrollUpButtonElement,
	SelectScrollUpButtonProps
>((props: ScopedProps<SelectScrollUpButtonProps>, forwardedRef) => {
	const contentContext = useSelectContentContext(
		SCROLL_UP_BUTTON_NAME,
		props.__scopeSelect,
	);
	const viewportContext = useSelectViewportContext(
		SCROLL_UP_BUTTON_NAME,
		props.__scopeSelect,
	);
	const [canScrollUp, setCanScrollUp] = React.useState(false);
	const composedRefs = useComposedRefs(
		forwardedRef,
		viewportContext.onScrollButtonChange,
	);

	useLayoutEffect(() => {
		if (contentContext.viewport && contentContext.isPositioned) {
			const { viewport } = contentContext;
			function handleScroll() {
				const canScrollUp = viewport.scrollTop > 0;
				setCanScrollUp(canScrollUp);
			}
			handleScroll();
			viewport.addEventListener("scroll", handleScroll);
			return () => viewport.removeEventListener("scroll", handleScroll);
		}
	}, [contentContext.viewport, contentContext.isPositioned]);

	return canScrollUp ? (
		<SelectScrollButtonImpl
			{...props}
			ref={composedRefs}
			onAutoScroll={() => {
				const { viewport, selectedItem } = contentContext;
				if (viewport && selectedItem) {
					viewport.scrollTop = viewport.scrollTop - selectedItem.offsetHeight;
				}
			}}
		/>
	) : null;
});

SelectScrollUpButtonPrimitive.displayName = SCROLL_UP_BUTTON_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectScrollDownButton
 * -----------------------------------------------------------------------------------------------*/

const SCROLL_DOWN_BUTTON_NAME = "SelectScrollDownButton";

type SelectScrollDownButtonElement = SelectScrollButtonImplElement;
type SelectScrollDownButtonProps = Omit<
	SelectScrollButtonImplProps,
	"onAutoScroll"
>;

const SelectScrollDownButtonPrimitive = React.forwardRef<
	SelectScrollDownButtonElement,
	SelectScrollDownButtonProps
>((props: ScopedProps<SelectScrollDownButtonProps>, forwardedRef) => {
	const contentContext = useSelectContentContext(
		SCROLL_DOWN_BUTTON_NAME,
		props.__scopeSelect,
	);
	const viewportContext = useSelectViewportContext(
		SCROLL_DOWN_BUTTON_NAME,
		props.__scopeSelect,
	);
	const [canScrollDown, setCanScrollDown] = React.useState(false);
	const composedRefs = useComposedRefs(
		forwardedRef,
		viewportContext.onScrollButtonChange,
	);

	useLayoutEffect(() => {
		if (contentContext.viewport && contentContext.isPositioned) {
			const { viewport } = contentContext;
			function handleScroll() {
				const maxScroll = viewport.scrollHeight - viewport.clientHeight;
				// we use Math.ceil here because if the UI is zoomed-in
				// `scrollTop` is not always reported as an integer
				const canScrollDown = Math.ceil(viewport.scrollTop) < maxScroll;
				setCanScrollDown(canScrollDown);
			}
			handleScroll();
			viewport.addEventListener("scroll", handleScroll);
			return () => viewport.removeEventListener("scroll", handleScroll);
		}
	}, [contentContext.viewport, contentContext.isPositioned]);

	return canScrollDown ? (
		<SelectScrollButtonImpl
			{...props}
			ref={composedRefs}
			onAutoScroll={() => {
				const { viewport, selectedItem } = contentContext;
				if (viewport && selectedItem) {
					viewport.scrollTop = viewport.scrollTop + selectedItem.offsetHeight;
				}
			}}
		/>
	) : null;
});

SelectScrollDownButtonPrimitive.displayName = SCROLL_DOWN_BUTTON_NAME;

type SelectScrollButtonImplElement = React.ComponentRef<typeof Primitive.div>;
interface SelectScrollButtonImplProps extends PrimitiveDivProps {
	onAutoScroll(): void;
}

const SelectScrollButtonImpl = React.forwardRef<
	SelectScrollButtonImplElement,
	SelectScrollButtonImplProps
>((props: ScopedProps<SelectScrollButtonImplProps>, forwardedRef) => {
	const { __scopeSelect, onAutoScroll, ...scrollIndicatorProps } = props;
	const contentContext = useSelectContentContext(
		"SelectScrollButton",
		__scopeSelect,
	);
	const autoScrollTimerRef = React.useRef<number | null>(null);
	const getItems = useCollection(__scopeSelect);

	const clearAutoScrollTimer = React.useCallback(() => {
		if (autoScrollTimerRef.current !== null) {
			window.clearInterval(autoScrollTimerRef.current);
			autoScrollTimerRef.current = null;
		}
	}, []);

	React.useEffect(() => {
		return () => clearAutoScrollTimer();
	}, [clearAutoScrollTimer]);

	// When the viewport becomes scrollable on either side, the relevant scroll button will mount.
	// Because it is part of the normal flow, it will push down (top button) or shrink (bottom button)
	// the viewport, potentially causing the active item to now be partially out of view.
	// We re-run the `scrollIntoView` logic to make sure it stays within the viewport.
	useLayoutEffect(() => {
		const activeItem = getItems().find(
			(item) => item.ref.current === document.activeElement,
		);
		activeItem?.ref.current?.scrollIntoView({ block: "nearest" });
	}, [getItems]);

	return (
		<Primitive.div
			aria-hidden
			{...scrollIndicatorProps}
			ref={forwardedRef}
			style={{ flexShrink: 0, ...scrollIndicatorProps.style }}
			onPointerDown={composeEventHandlers(
				scrollIndicatorProps.onPointerDown,
				() => {
					if (autoScrollTimerRef.current === null) {
						autoScrollTimerRef.current = window.setInterval(onAutoScroll, 50);
					}
				},
			)}
			onPointerMove={composeEventHandlers(
				scrollIndicatorProps.onPointerMove,
				() => {
					contentContext.onItemLeave?.();
					if (autoScrollTimerRef.current === null) {
						autoScrollTimerRef.current = window.setInterval(onAutoScroll, 50);
					}
				},
			)}
			onPointerLeave={composeEventHandlers(
				scrollIndicatorProps.onPointerLeave,
				() => {
					clearAutoScrollTimer();
				},
			)}
		/>
	);
});

/* -------------------------------------------------------------------------------------------------
 * SelectSeparator
 * -----------------------------------------------------------------------------------------------*/

const SEPARATOR_NAME = "SelectSeparator";

type SelectSeparatorElement = React.ComponentRef<typeof Primitive.div>;
type SelectSeparatorProps = PrimitiveDivProps;

/**
 * Select.Separator component for separating groups of items in the select dropdown
 *
 * This component renders a horizontal line to visually separate different sections or groups within the dropdown.
 */
const SelectSeparatorPrimitive = React.forwardRef<
	SelectSeparatorElement,
	SelectSeparatorProps
>((props: ScopedProps<SelectSeparatorProps>, forwardedRef) => {
	const { __scopeSelect, ...separatorProps } = props;
	return <Primitive.div aria-hidden {...separatorProps} ref={forwardedRef} />;
});

SelectSeparatorPrimitive.displayName = SEPARATOR_NAME;

/* -------------------------------------------------------------------------------------------------
 * SelectArrow
 * -----------------------------------------------------------------------------------------------*/

const ARROW_NAME = "SelectArrow";

type SelectArrowElement = React.ComponentRef<typeof PopperArrow>;
type PopperArrowProps = React.ComponentPropsWithoutRef<typeof PopperArrow>;
type SelectArrowProps = PopperArrowProps;

const SelectArrow = React.forwardRef<SelectArrowElement, SelectArrowProps>(
	(props: ScopedProps<SelectArrowProps>, forwardedRef) => {
		const { __scopeSelect, ...arrowProps } = props;
		const popperScope = usePopperScope(__scopeSelect);
		const context = useSelectContext(ARROW_NAME, __scopeSelect);
		const contentContext = useSelectContentContext(ARROW_NAME, __scopeSelect);
		return context.open && contentContext.position === "popper" ? (
			<PopperArrow {...popperScope} {...arrowProps} ref={forwardedRef} />
		) : null;
	},
);

SelectArrow.displayName = ARROW_NAME;

/* -----------------------------------------------------------------------------------------------*/

function shouldShowPlaceholder(value?: string) {
	return value === "" || value === undefined;
}

const BubbleSelect = React.forwardRef<
	HTMLSelectElement,
	React.ComponentPropsWithoutRef<"select">
>((props, forwardedRef) => {
	const { value, ...selectProps } = props;
	const ref = React.useRef<HTMLSelectElement>(null);
	const composedRefs = useComposedRefs(forwardedRef, ref);
	const prevValue = usePrevious(value);

	// Bubble value change to parents (e.g form change event)
	React.useEffect(() => {
		const select = ref.current!;
		const selectProto = window.HTMLSelectElement.prototype;
		const descriptor = Object.getOwnPropertyDescriptor(
			selectProto,
			"value",
		) as PropertyDescriptor;
		const setValue = descriptor.set;
		if (prevValue !== value && setValue) {
			const event = new Event("change", { bubbles: true });
			setValue.call(select, value);
			select.dispatchEvent(event);
		}
	}, [prevValue, value]);

	/**
	 * We purposefully use a `select` here to support form autofill as much
	 * as possible.
	 *
	 * We purposefully do not add the `value` attribute here to allow the value
	 * to be set programmatically and bubble to any parent form `onChange` event.
	 * Adding the `value` will cause React to consider the programmatic
	 * dispatch a duplicate and it will get swallowed.
	 *
	 * We use `VisuallyHidden` rather than `display: "none"` because Safari autofill
	 * won't work otherwise.
	 */
	return (
		<VisuallyHidden asChild>
			<select {...selectProps} ref={composedRefs} defaultValue={value} />
		</VisuallyHidden>
	);
});

BubbleSelect.displayName = "BubbleSelect";

function useTypeaheadSearch(onSearchChange: (search: string) => void) {
	const handleSearchChange = useCallbackRef(onSearchChange);
	const searchRef = React.useRef("");
	const timerRef = React.useRef(0);

	const handleTypeaheadSearch = React.useCallback(
		(key: string) => {
			const search = searchRef.current + key;
			handleSearchChange(search);

			(function updateSearch(value: string) {
				searchRef.current = value;
				window.clearTimeout(timerRef.current);
				// Reset `searchRef` 1 second after it was last updated
				if (value !== "") {
					timerRef.current = window.setTimeout(() => updateSearch(""), 1000);
				}
			})(search);
		},
		[handleSearchChange],
	);

	const resetTypeahead = React.useCallback(() => {
		searchRef.current = "";
		window.clearTimeout(timerRef.current);
	}, []);

	React.useEffect(() => {
		return () => window.clearTimeout(timerRef.current);
	}, []);

	return [searchRef, handleTypeaheadSearch, resetTypeahead] as const;
}

/**
 * This is the "meat" of the typeahead matching logic. It takes in a list of items,
 * the search and the current item, and returns the next item (or `undefined`).
 *
 * We normalize the search because if a user has repeatedly pressed a character,
 * we want the exact same behavior as if we only had that one character
 * (ie. cycle through items starting with that character)
 *
 * We also reorder the items by wrapping the array around the current item.
 * This is so we always look forward from the current item, and picking the first
 * item will always be the correct one.
 *
 * Finally, if the normalized search is exactly one character, we exclude the
 * current item from the values because otherwise it would be the first to match always
 * and focus would never move. This is as opposed to the regular case, where we
 * don't want focus to move if the current item still matches.
 */
function findNextItem<T extends { textValue: string }>(
	items: T[],
	search: string,
	currentItem?: T,
) {
	const isRepeated =
		search.length > 1 && Array.from(search).every((char) => char === search[0]);
	const normalizedSearch = isRepeated ? search[0] : search;
	const currentItemIndex = currentItem ? items.indexOf(currentItem) : -1;
	let wrappedItems = wrapArray(items, Math.max(currentItemIndex, 0));
	const excludeCurrentItem = normalizedSearch.length === 1;
	if (excludeCurrentItem) {
		wrappedItems = wrappedItems.filter((v) => v !== currentItem);
	}
	const nextItem = wrappedItems.find((item) =>
		item.textValue.toLowerCase().startsWith(normalizedSearch.toLowerCase()),
	);
	return nextItem !== currentItem ? nextItem : undefined;
}

/**
 * Wraps an array around itself at a given start index
 * Example: `wrapArray(['a', 'b', 'c', 'd'], 2) === ['c', 'd', 'a', 'b']`
 */
function wrapArray<T>(array: T[], startIndex: number) {
	return array.map((_, index) => array[(startIndex + index) % array.length]);
}

const SelectTrigger = React.forwardRef<
	React.ComponentRef<typeof SelectTriggerPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectTriggerPrimitive>
>(({ className, children, ...props }, ref) => (
	<SelectTriggerPrimitive
		ref={ref}
		className={cn(
			"flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
			className,
		)}
		{...props}
	>
		{children}
		<SelectIcon asChild>
			<ChevronDown className="h-4 w-4 opacity-50" />
		</SelectIcon>
	</SelectTriggerPrimitive>
));
SelectTrigger.displayName = SelectTriggerPrimitive.displayName;

const SelectScrollUpButton = React.forwardRef<
	React.ComponentRef<typeof SelectScrollUpButtonPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectScrollUpButtonPrimitive>
>(({ className, ...props }, ref) => (
	<SelectScrollUpButtonPrimitive
		ref={ref}
		className={cn(
			"flex cursor-default items-center justify-center py-1",
			className,
		)}
		{...props}
	>
		<ChevronUp className="h-4 w-4" />
	</SelectScrollUpButtonPrimitive>
));
SelectScrollUpButton.displayName = SelectScrollUpButtonPrimitive.displayName;

const SelectScrollDownButton = React.forwardRef<
	React.ComponentRef<typeof SelectScrollDownButtonPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectScrollDownButtonPrimitive>
>(({ className, ...props }, ref) => (
	<SelectScrollDownButtonPrimitive
		ref={ref}
		className={cn(
			"flex cursor-default items-center justify-center py-1",
			className,
		)}
		{...props}
	>
		<ChevronDown className="h-4 w-4" />
	</SelectScrollDownButtonPrimitive>
));
SelectScrollDownButton.displayName =
	SelectScrollDownButtonPrimitive.displayName;

const SelectContent = React.forwardRef<
	React.ComponentRef<typeof SelectContentPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectContentPrimitive>
>(({ className, children, position = "popper", ...props }, ref) => (
	<SelectPortal>
		<SelectContentPrimitive
			ref={ref}
			className={cn(
				"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in",
				position === "popper" &&
					"data-[side=left]:-translate-x-1 data-[side=top]:-translate-y-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1",
				className,
			)}
			position={position}
			{...props}
		>
			<SelectScrollUpButton />
			<SelectViewport
				className={cn(
					"p-1",
					position === "popper" &&
						"h-[var(--squared-select-trigger-height)] w-full min-w-[var(--squared-select-trigger-width)]",
				)}
			>
				{children}
			</SelectViewport>
			<SelectScrollDownButton />
		</SelectContentPrimitive>
	</SelectPortal>
));
SelectContent.displayName = SelectContentPrimitive.displayName;

const SelectLabel = React.forwardRef<
	React.ComponentRef<typeof SelectLabelPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectLabelPrimitive>
>(({ className, ...props }, ref) => (
	<SelectLabelPrimitive
		ref={ref}
		className={cn("py-1.5 pr-2 pl-8 font-semibold text-sm", className)}
		{...props}
	/>
));
SelectLabel.displayName = SelectLabelPrimitive.displayName;

const SelectItem = React.forwardRef<
	React.ComponentRef<typeof SelectItemPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectItemPrimitive>
>(({ className, children, ...props }, ref) => (
	<SelectItemPrimitive
		ref={ref}
		className={cn(
			"relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
			className,
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<SelectItemIndicator>
				<Check className="h-4 w-4" />
			</SelectItemIndicator>
		</span>

		<SelectItemText>{children}</SelectItemText>
	</SelectItemPrimitive>
));
SelectItem.displayName = SelectItemPrimitive.displayName;

const SelectSeparator = React.forwardRef<
	React.ComponentRef<typeof SelectSeparatorPrimitive>,
	React.ComponentPropsWithoutRef<typeof SelectSeparatorPrimitive>
>(({ className, ...props }, ref) => (
	<SelectSeparatorPrimitive
		ref={ref}
		className={cn("-mx-1 my-1 h-px bg-muted", className)}
		{...props}
	/>
));
SelectSeparator.displayName = SelectSeparatorPrimitive.displayName;

export {
	createSelectScope,
	//
	Select,
	SelectTrigger,
	SelectValue,
	SelectIcon,
	SelectPortal,
	SelectContent,
	SelectViewport,
	SelectGroup,
	SelectLabel,
	SelectItem,
	SelectItemText,
	SelectItemIndicator,
	SelectScrollUpButton,
	SelectScrollDownButton,
	SelectSeparator,
	SelectArrow,
};
export type {
	SelectProps,
	SelectTriggerProps,
	SelectValueProps,
	SelectIconProps,
	SelectPortalProps,
	SelectContentProps,
	SelectViewportProps,
	SelectGroupProps,
	SelectLabelProps,
	SelectItemProps,
	SelectItemTextProps,
	SelectItemIndicatorProps,
	SelectScrollUpButtonProps,
	SelectScrollDownButtonProps,
	SelectSeparatorProps,
	SelectArrowProps,
};
